import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import get from "lodash/get"
import { useEffect } from "react"
import styled from "styled-components"
import { OrderFragment, useResourceLicenseQuery } from "@pathwright/graphql-client/src/generated"
import { useCurrencyFormatter } from "../currency/CurrencyFormatterProvider"
import { getGroupUrl } from "../utils/urls"
import OrderProcessing, { OrderProcessingLoading } from "./OrderProcessing"
import { OnOrderFulfilledCallback } from "./useOrderPollingQuery"

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
`

const CenteredContent = styled.div`
  padding: 40px 25px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;
`

const StyledBlankSlate = styled(BlankSlate)`
  > * {
    justify-content: flex-start;
  }
`

function useOrderResourceLicense(order: OrderFragment) {
  const resourceLicenseQuery = useResourceLicenseQuery({
    skip: !order || !order.fulfilled_object_id,
    variables: {
      id: order && order.fulfilled_object_id
    }
  })
  const resourceLicense = resourceLicenseQuery.data?.resource_license || null
  return resourceLicense
}

export function GroupLicenseOrderConfirmationContent({
  order
}: {
  order: OrderFragment
}) {
  const resourceLicense = useOrderResourceLicense(order)
  const resource = resourceLicense?.group?.resource || null

  useEffect(() => {
    if (get(order, "order_status") === "fulfilled") {
      // HACK: bootstrapping the store will ensure the
      // window.bootstrappedData.permissions.user_is_teaching flag will be updated
      window.App && window.App.getStore("school").action.bootstrapSchool()
    }
  }, [get(order, "order_status")])

  return resourceLicense && resource ? (
    <StyledBlankSlate
      animateIcon
      stack
      icons={null}
      icon="check"
      heading="Ready To Teach!"
      body={`Congratulations, you're ready to start taking ${resource.name} with your group. You'll receive an email confirmation and receipt shortly.`}
      primaryAction={
        resourceLicense
          ? {
              to: getGroupUrl(
                resource.slug,
                resourceLicense.group?.id || 0,
                "path"
              ),
              children: "Get Started"
            }
          : null
      }
    />
  ) : (
    <OrderProcessingLoading />
  )
}

export function GroupLicenseOrderConfirmation({
  orderId,
  onOrderFulfilled
}: {
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}) {
  return (
    <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
      {({ order }) => <GroupLicenseOrderConfirmationContent order={order} />}
    </OrderProcessing>
  )
}

function GroupLicenseOrderConfirmationCardHeader({
  order,
  card
}: {
  order: OrderFragment
  card: any
}) {
  const { tc } = useTranslate()
  const { currencyFormatter } = useCurrencyFormatter()
  const resourceLicense = useOrderResourceLicense(order)
  const resource = resourceLicense?.group?.resource || null
  const license = resourceLicense?.license_offering || null

  return resource && license ? (
    <CardHeader
      card={card}
      resource={resource}
      title={resource.name}
      meta={
        (license.cost_per_seat || 0) > 0
          ? `Create Your Own ${tc(
              "cohort.label"
            )} • ${currencyFormatter.customFormat(
              license.cost_per_seat
            )}/member`
          : `Create Your Own ${tc("cohort.label")}`
      }
    />
  ) : null
}

export default function GroupLicenseOrderConfirmationCard({
  card,
  orderId,
  onOrderFulfilled
}: {
  card: any
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}) {
  return (
    <NarrowCard card={card} navless>
      <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
        {({ order }) => (
          <>
            <GroupLicenseOrderConfirmationCardHeader
              order={order}
              card={card}
            />
            <GroupLicenseOrderConfirmationContent order={order} />
          </>
        )}
      </OrderProcessing>
    </NarrowCard>
  )
}

GroupLicenseOrderConfirmationCard.displayName =
  "GroupLicenseOrderConfirmationCard"
